import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router";
import { useAuth, useTextDomainContext } from "@/app/providers";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDocumentTypeArray, useEnergyTypeArray } from "@/hooks";
import { formatDateShort } from "@/utils";
import { string } from "prop-types";

type DocumentViewProps = {
  parentPath?: string;
};

export const DocumentView = (props: DocumentViewProps) => {
  const location = useLocation();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const documentTypes = useDocumentTypeArray();
  const energyTypes = useEnergyTypeArray();
  const { token } = useAuth();
  const parentPath = props.parentPath ? `/${props.parentPath}` : "";

  // Get document data from location state
  const document = location?.state?.row || null;

  // State for PDF blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  // Generate download URL (without inline parameter)
  const downloadUrl = useMemo(() => {
    if (!document) return "";
    const apiUrl = import.meta.env.VITE_API_URL;
    return `${apiUrl}/v2/reports/download_user_document?document_id=${document.id}`;
  }, [document]);

  // Fetch PDF as blob and create object URL
  useEffect(() => {
    if (!document || !downloadUrl) return;

    let objectUrl = "";

    const fetchPdf = async () => {
      setLoadingPdf(true);
      setPdfError(false);

      try {
        const headers: Record<string, string> = {};
        if (token) {
          headers.authorization = `Bearer ${token}`;
        }

        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: headers,
          credentials: "include",
        });

        if (!response.ok) {
          setPdfError(true);
          return;
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objectUrl);
      } catch {
        setPdfError(true);
      } finally {
        setLoadingPdf(false);
      }
    };

    fetchPdf().then();

    // Cleanup: revoke the object URL when component unmounts
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [document, downloadUrl, token]);

  // Get labels for energy type and document type
  const getEnergyTypeLabel = (value: string) => {
    const energyType = energyTypes.find((type) => type.value === value);
    return energyType ? energyType.label : value;
  };

  const getDocumentTypeLabel = (value: string) => {
    const docType = documentTypes.find((type) => type.value === value);
    return docType ? docType.label : value;
  };

  if (!document) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: "error" }}>
          {gettext("Document not found")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Back Button */}
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 3 }}>
        <Button
          component={Link}
          to={`${parentPath}/documents`}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          {gettext("Back to Documents")}
        </Button>
        <Typography variant="h4" component="h1">
          {document.name}
        </Typography>
      </Stack>

      {/* Document Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {gettext("Document Details")}
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("Name")}
              </Typography>
              <Typography variant="body1">{document.name || "-"}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("Description")}
              </Typography>
              <Typography variant="body1">
                {document.description || "-"}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("Energy Type")}
              </Typography>
              <Typography variant="body1">
                {getEnergyTypeLabel(document.energyType)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("Document Type")}
              </Typography>
              <Typography variant="body1">
                {getDocumentTypeLabel(document.documentType)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("Start Date")}
              </Typography>
              <Typography variant="body1">
                {document.startDate
                  ? formatDateShort(new Date(document.startDate))
                  : "-"}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("End Date")}
              </Typography>
              <Typography variant="body1">
                {document.endDate
                  ? formatDateShort(new Date(document.endDate))
                  : "-"}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {gettext("File Name")}
              </Typography>
              <Typography variant="body1">
                {document.originalFileName || document.fileName || "-"}
              </Typography>
            </Grid>
          </Grid>

          {/* Download Button */}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {gettext("Download Document")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* PDF Preview */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {gettext("Document Preview")}
          </Typography>

          {loadingPdf ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : pdfError ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <Typography sx={{ color: "error" }}>
                {gettext(
                  "Failed to load document preview. Please try downloading the document.",
                )}
              </Typography>
            </Box>
          ) : pdfBlobUrl ? (
            <Box
              sx={{
                width: "100%",
                height: "800px",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                mt: 2,
              }}
            >
              <iframe
                src={`${pdfBlobUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title={gettext("Document Preview")}
              />
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              {gettext("No document available for preview")}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

DocumentView.propTypes = {
  parentPath: string,
};
